<template>
  <main
    id="app"
    :class="[
      'container',
      isHubspotIntegrationEnabled && isShopPage && 'container--shop-hubspot-bg',
      !isHubspotIntegrationEnabled && isShopPage && 'container--shop',
      !isHubspotIntegrationEnabled && showDefaultHeroSection && 'container--shop-default-hero',
      showEDAPremiumPreSaleHeroSection && 'container--eda-premium-pre-sale',
      showBlackFridayHeroSection && 'container--black-friday-hero',
      showLeapYearPromoHeroSection && 'container--leap-year-promo',
      showTCSPromoHeroSection && 'container--tcs-promo',
      showFlashSaleHeroSection && 'container--flash-sale',
      showBoogoHeroSection && 'container--boogo',
      showSpringHeroSection && 'container--spring',
      showPremiumPlusHeroSection && 'container--premium-plus',
      showMaySubscriptionHeroSection && 'container--may-subscription',
      showMayFlashSaleHeroSection && 'container--may-flash-sale',
      showSummerSavingsHeroSection && 'container--summer-savings',
      !isHubspotIntegrationEnabled && showEcirPreSaleHeroSection && 'container--ecir-presale',
      showJulySaleHeroSection && 'container--july-sale',
      !isShopPage && `container--${routeName}`,
    ]"
  >
    <div v-if="isModalOpen" class="container__mask"></div>
    <TheHeader v-if="isHeaderShown" />
    <ShopHeader v-if="isShopPage" />
    <ShopSectionStickyNavBar
      v-if="isShopPage"
      sticky-bar
      :customShopContainerName="stickyNavContainerClassName"
    />
    <HeroSection v-if="showHeroSection" @hero-section-to-show="setHeroSectionToShow" />
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" :key="$route.name" />
      </transition>
    </router-view>
    <PaymentForm v-if="isPaymentModalShown" />
    <PaymentSelectorModal v-if="isPaymentSelectorModalShown" />
    <SignInModal v-if="isSignInModalShown" />
    <div id="recurly-3ds" :style="isIn3dsFlow ? 'display: block' : 'display: none'"></div>
  </main>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'
import TheHeader from '@/components/flow/TheHeader.vue'
import ShopHeader from '@/components/flow/ShopHeader.vue'
import ShopSectionStickyNavBar from '@/components/flow/ShopSectionStickyNavBar.vue'
import PaymentForm from '@/components/payment/form/PaymentForm.vue'
import PaymentSelectorModal from '@/components/payment/PaymentSelectorModal.vue'
import HeroSection from '@/components/shop-sections/HeroSection.vue'
import SignInModal from '@/components/login/SignInModal.vue'
import { routesRules } from '@/helpers/mixins'

export default {
  name: 'MainLayout',
  mixins: [routesRules],
  components: {
    TheHeader,
    ShopHeader,
    PaymentForm,
    PaymentSelectorModal,
    HeroSection,
    SignInModal,
    ShopSectionStickyNavBar,
  },
  data() {
    return {
      heroSectionToShow: 'default',
    }
  },
  computed: {
    ...mapState({
      isIn3dsFlow: state => state.subscriptions.isIn3dsFlow,
      isEditModalShown: state => state.app.isEditModalShown,
      isPaymentModalShown: state => state.app.isPaymentModalShown,
      isPaymentSelectorModalShown: state => state.app.isPaymentSelectorModalShown,
      isSignInModalShown: state => state.app.isSignInModalShown,
    }),
    ...mapGetters({
      isSummerSavingsEnabled: 'featureFlags/isSummerSavingsEnabled',
      isHubspotIntegrationEnabled: 'featureFlags/isHubspotIntegrationEnabled',
      isBlackFridayActive: 'featureFlags/isBlackFridayActive',
    }),
    isModalOpen() {
      return (
        this.isIn3dsFlow ||
        this.isEditModalShown ||
        this.isPaymentModalShown ||
        this.isPaymentSelectorModalShown ||
        this.isSignInModalShown
      )
    },
    showDefaultHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'default'
    },
    showEDAPremiumPreSaleHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'eeda-presale'
    },
    showBoogoHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'boogo'
    },
    showPremiumMonthlyHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'premium-monthly'
    },
    showBlackFridayHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'black-friday'
    },
    showLeapYearPromoHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'leap-year-2024'
    },
    showTCSPromoHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'tcs-promo'
    },
    showFlashSaleHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'flash-sale'
    },
    showSpringHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'spring'
    },
    showPremiumPlusHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'premium-plus'
    },
    showMaySubscriptionHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'may-sale'
    },
    showMayFlashSaleHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'may-flash-sale'
    },
    showSummerSavingsHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'summer-savings'
    },
    showEcirPreSaleHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'ecir-presale'
    },
    showJulySaleHeroSection() {
      return this.showHeroSection && this.heroSectionToShow === 'july-sale'
    },
    stickyNavContainerClassName() {
      if (this.isSummerSavingsEnabled) {
        return 'summer-savings-shop'
      }
      if (this.isBlackFridayActive) {
        return 'black-friday'
      }
      return undefined
    },
  },
  methods: {
    ...mapMutations({
      setIsEditModalShown: 'app/IS_EDIT_MODAL_SHOWN_UPDATED',
    }),
    setHeroSectionToShow(heroSectionToShow) {
      this.heroSectionToShow = heroSectionToShow
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  $main_bg: '../assets/images/backgrounds/main_bg.jpg';
  $bg-diamonds: '../assets/images/backgrounds/bg-diamonds.png';
  $shop_bg: '../assets/images/backgrounds/shop_bg.png';
  $shop-header-bg: '../assets/images/backgrounds/shop-header-bg-1.png';
  $pre-sale-bg: '../assets/images/backgrounds/cert_pre_sale_bg.png';
  display: flex;
  flex-flow: column;
  padding: 0 !important;
  position: relative;
  width: 100%;
  &--shop {
    background: url(#{$shop_bg}), var(--black-2) url(#{$main_bg});
    background-repeat: no-repeat;
    background-size: cover;
    min-height: 150vh;
    &-hubspot-bg {
      background: var(--black);
      min-height: 150vh;
    }
  }
  &--complete {
    min-height: unset;
    height: 100%;
    @include mobile {
      position: absolute;
    }
  }
  &--eda-premium-pre-sale {
    background: var(--black-1) !important;
  }
  &--shop-default-hero {
    @include desktop {
      min-height: unset;
      background: url(#{$shop-header-bg}), var(--black-2) url(#{$main_bg});
      background-repeat: no-repeat, no-repeat;
      background-position: top, top;
      background-size: 100%, cover;
    }
    @include lg-desktop {
      background-size: 100%, cover;
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      width: 100%;
      background: linear-gradient(0deg, #7d47b800 0%, #111111de 10%, #111111de 60%, #7d47b800 100%);
      z-index: 1;
    }

    @media screen and (min-width: 1350px) {
      &::after {
        top: 50vh;
        height: 80vh;
      }
    }

    @media screen and (min-width: 1440px) {
      &::after {
        top: 40vh;
        height: 80vh;
      }
    }
  }
  &--black-friday-hero {
    background: var(--dark-brighter);
    overflow-x: hidden;
    &:before {
      content: '';
      position: absolute;
      top: 450px;
      left: 450px;
      width: 325.589px;
      height: 454.286px;
      transform: rotate(90deg);
      border-radius: 468px;
      background: var(--purple);
      filter: blur(157px);
      z-index: 0;
    }
    &:after {
      content: '';
      position: absolute;
      top: 300px;
      left: -400px;
      width: 368.026px;
      height: 513.595px;
      transform: rotate(90deg);
      border-radius: 529px;
      background: var(--blue);
      filter: blur(157px);
      z-index: 0;
    }
    @include tablet {
      &:before {
        transform: matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
        top: 27px;
        left: -72px;
        width: 468px;
        height: 360px;
      }
      &:after {
        transform: matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
        top: 208px;
        left: -160px;
        width: 529px;
        height: 407px;
      }
    }
    @include mobile {
      &:before {
        display: none;
      }
      &:after {
        top: 200px;
        left: -100px;
        width: 529px;
        height: 407px;
      }
    }
  }
  &--tcs-promo,
  &--leap-year-promo {
    background: linear-gradient(180deg, rgba(1, 0, 1, 0.7) 70%, rgba(1, 0, 1, 0) 100%),
      radial-gradient(circle, rgba(18, 70, 201, 0.7) 0%, rgba(18, 70, 201, 0) 70%),
      radial-gradient(circle, rgb(243, 76, 217) -5%, rgba(243, 76, 217, 0) 70%),
      var(--black-2) url(#{$main_bg});
    background-repeat: no-repeat;
    background-size: 750px 700px, 750px 700px, 750px 700px, cover;
    background-position: 0 0, -190px 100px, -190px -240px, 0 0;
    @include mobile {
      background: radial-gradient(circle, rgba(18, 70, 201, 70%) 0%, rgba(18, 70, 201, 0) 70%),
        radial-gradient(circle, rgba(243, 76, 217, 100%) -5%, rgba(243, 76, 217, 0) 70%),
        linear-gradient(180deg, rgba(1, 0, 1, 1) 70%, rgba(1, 0, 1, 0) 100%),
        var(--black-2) url(#{$main_bg});
      background-repeat: no-repeat;
      background-size: 650px 700px, 650px 700px, 100% 700px, cover;
      background-position: -400px 80px, -400px -160px, 0 0, 0 0;
    }
    @include desktop {
      background-size: 850px 800px, 700px 700px, 100% 700px, cover;
      background-position: -230px 50px, -160px -100px, 0 0, 0 0;
    }
    @include lg-desktop {
      background-size: 1500px 1500px, 1500px 1500px, 100% 1500px, cover;
      background-position: 0 -200px, 0 -600px, 0 0, 0 0;
    }
  }
  &--flash-sale {
    background: var(--gray-purple);
  }
  &--spring {
    background: var(--charcoal);
  }
  &--premium-plus {
    background: var(--gray-purple);
  }
  &--may-subscription {
    background: var(--charcoal);
  }
  &--may-flash-sale {
    background: var(--charcoal);
  }
  &--summer-savings {
    background: var(--charcoal);
  }
  &--ecir-presale {
    background-repeat: no-repeat;
    background-size: contain;
    background-position-x: 150px;
    background-color: var(--dark-brighter);
    background-image: url(#{$pre-sale-bg});
  }
  &--july-sale {
    background: var(--dark-brighter);
  }
  .shop {
    z-index: 2;
  }
}
</style>
